import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  /** Mensaje de error: se pinta JUNTO al campo, nunca solo arriba del formulario. */
  error?: string
  hint?: ReactNode
  containerClassName?: string
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, hint, required, className, containerClassName, ...props },
  ref,
) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {/* Etiqueta siempre visible: el placeholder como etiqueta desaparece al escribir. */}
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-label="obligatorio">
            *
          </span>
        )}
      </label>

      <input
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(error && errorId, hint && hintId) || undefined}
        className={cn(
          'h-11 w-full rounded-lg border bg-card px-3 text-sm text-foreground lg:h-10',
          'placeholder:text-muted-foreground',
          'transition-colors duration-150',
          'focus-visible:outline-2 focus-visible:outline-offset-[-1px] focus-visible:outline-ring',
          'disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60',
          error ? 'border-danger' : 'border-input',
          className,
        )}
        {...props}
      />

      {hint && !error && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
})
